'use client';
import type { ElementType } from 'react';
import { Button, DrawerHeader, DrawerTitle, Link } from '@skin-seoul/design-system';
import {
  TEMP_ITEM_KEY,
  getCartItemQuantityBounds,
  useCartService,
  useRegionCurrency,
  useAnalyticsEvent,
  type CartItemResponse,
  type CartResponseResponse,
  type Currency,
  CLASSNAME,
  getFilteredHalloweenPromoItems,
  getCartSummary,
  getCartBannerRewardGroup,
  getCartAnalyticsContext,
} from '@skin-seoul/service';
import { Condition, usePersonalizationTracker } from '@skin-seoul/utils';
import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useCartDiscovery } from '../components/CartDiscoveryNavigation';
import { CartRewardUpsellSkeleton } from '../components/CartRewardUpsell.Skeleton';
import { CartRewardDiscovery, CartRewardUpsell, getVisibleCartRewardGroup } from '../components/CartRewardDiscovery';
import { CartBenefitsBanner } from '../components/CartBenefitsBanner';
import { CartBenefitsBannerSkeleton } from '../components/CartBenefitsBanner.Skeleton';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import QueryBoundary from '../../../utils/react/QueryBoundary';
import { CartExpectedCreditRow } from '../components/CartExpectedCreditRow';
import CartOrderSummary from '../components/CartOrderSummary';
import { CartProductCardWithPacks } from '../components/CartProductCardWithPacks';
import CartProductCard from '../components/CartProductCard';
import { CartTrustBadges } from '../components/CartTrustBadges';
import { CartWishlist, CartRecentlyViewed } from '../components/CartSavedProducts';
import { CartSkinAnalysisRoutine } from '../components/CartSkinAnalysisRoutine';
import { CartRecommendations } from '../components/CartRecommendations';
import { CartRecommendationsSkeleton } from '../components/CartRecommendations.Skeleton';
import { useCartAnalytics } from '../components/CartAnalytics';
import { useCartQuantityQueue } from './useCartQuantityQueue';

const TRUST_TRANSITION_SECONDS = 0.24;

type CartWithItemViewProps = {
  currency: Currency;
  onCheckoutClick?: () => void;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
  linkComponent?: ElementType;
};

export const CartWithItemView = ({
  currency,
  onCheckoutClick,
  ImageComponent,
  linkComponent,
}: CartWithItemViewProps) => {
  const LinkComponent = linkComponent || Link;
  const { useGetCartSuspenseQuery, useDeleteCartItem, useUpdateCartItem } = useCartService();
  const { isOpen, onCartClose } = useCartDrawer();

  const { data } = useGetCartSuspenseQuery({ currency });
  const { country } = useRegionCurrency();
  const { presentation, open, isInitialLoading } = useCartDiscovery();
  const cartData = data?.cart;
  const bannerRewardGroup = getCartBannerRewardGroup(
    presentation?.groups ?? [],
    cartData?.progressBar?.freeShipping?.applied === true,
  );
  const rewardGroup = bannerRewardGroup ?? getVisibleCartRewardGroup(presentation);
  const { mutate: deleteItemMutate } = useDeleteCartItem({ currency });
  const { mutateAsync: updateItemMutate } = useUpdateCartItem({ currency });

  const tracker = usePersonalizationTracker();
  const analytics = useAnalyticsEvent();
  const cartAnalytics = useCartAnalytics();
  const shippingCost = cartData.shippingRates?.length > 0 ? cartData.shippingRates[0].price : 0;

  const { items, filteredCoupons, orderTotal, totalTax } = getCartSummary(cartData);

  const filteredItems = getFilteredHalloweenPromoItems(items);
  const totalProductPrice = filteredItems.reduce((prev, curr) => prev + curr.price.price * curr.quantity, 0);
  const totalProductQuantity = filteredItems.reduce((prev, curr) => prev + curr.quantity, 0);

  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const checkoutTracked = useRef(false);
  const quantityScope = JSON.stringify([currency, country, isOpen]);
  useEffect(() => {
    checkoutTracked.current = false;
  }, [quantityScope]);

  const trackItemRemoved = (itemKey: string, cart: CartResponseResponse['cart']) => {
    const item = filteredItems.find((candidate) => candidate.key === itemKey);
    if (item) {
      tracker.removeFromCart(item.productId.toString(), 'cart');
      analytics.trackRemoveFromCart({
        productId: String(item.productId),
        quantity: item.quantity,
        params: {
          ...getCartAnalyticsContext(cart, 'cart_drawer'),
          sku: item.sku,
          trigger_placement: 'cart_remove',
        },
      });
    }
    cartAnalytics.mutation(cartData, cart, 'cart_remove');
  };

  // Serialized batch writes compare each response with the previous one, starting from the cart
  // at schedule time; the queue flushes with the writer of the render that scheduled the batch.
  const createQuantityWriter = () => {
    let before = cartData;
    return async (itemKey: string, quantity: number) => {
      const response = await updateItemMutate({ itemKey, quantity, currency });
      cartAnalytics.mutation(before, response.cart, 'cart_quantity');
      before = response.cart;
      return response;
    };
  };

  const quantityQueue = useCartQuantityQueue({
    scope: quantityScope,
    items: filteredItems,
    updateItem: createQuantityWriter(),
    removeItem: (itemKey, callbacks) =>
      deleteItemMutate(
        { itemKey, currency },
        {
          onSuccess: (response) => {
            trackItemRemoved(itemKey, response.cart);
            callbacks.onSuccess();
          },
          onError: callbacks.onError,
        },
      ),
  });

  const handleQuantityChange = (item: CartItemResponse, quantity: number) => {
    if (!Number.isSafeInteger(quantity)) {
      return;
    }
    const { min: minimum, max: maximum } = getCartItemQuantityBounds(item);
    const nextQuantity = Math.max(minimum, Math.min(maximum, quantity));
    const previousQuantity = quantityQueue.getQueuedQuantity(item.key) ?? item.quantity;
    if (previousQuantity !== nextQuantity) {
      cartAnalytics.track('cart_item_quantity_changed', {
        product_id: item.productId,
        sku: item.sku,
        previous_quantity: previousQuantity,
        quantity: nextQuantity,
        quantity_delta: nextQuantity - previousQuantity,
        trigger_placement: 'cart_quantity',
        placement: 'cart_quantity',
      });
    }
    quantityQueue.schedule(item.key, nextQuantity);
  };

  const handleDeleteItem = (item: CartItemResponse) => {
    quantityQueue.remove(item.key);
  };

  const trackCheckoutClick = () =>
    cartAnalytics.track('button_pressed_x_checkout', {
      button_name: 'checkout',
      button_style: 'text',
      button_placement: 'bottom',
      button_location: 'cart view',
    });

  const handleCheckoutClick = async (event: React.MouseEvent<HTMLElement>) => {
    const settled = quantityQueue.settleBeforeCheckout();
    // A click while an earlier checkout still waits on writes neither logs nor resets the attempt.
    const isFirstAttempt = !checkoutTracked.current;
    if (isFirstAttempt) {
      checkoutTracked.current = true;
      trackCheckoutClick();
    }
    if (!settled) {
      onCheckoutClick?.();
      onCartClose();
      return;
    }
    event.preventDefault();
    const link = event.currentTarget;
    if ((await settled) && link.isConnected) {
      link.click();
      return;
    }
    if (isFirstAttempt) {
      checkoutTracked.current = false;
    }
  };

  return (
    <>
      <DrawerHeader className="items-center bg-generic-0 flex h-12 shrink-0 flex-row justify-between p-3 w-full">
        <DrawerTitle className="font-medium text-center text-label-large w-full">My Bag</DrawerTitle>
      </DrawerHeader>

      <div
        className="p-0 pb-4 w-full min-h-0 flex-1 overflow-y-auto scrollbar-none touch-pan-y"
        data-vaul-no-drag
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col w-full px-4 pt-2">
          <QueryBoundary clientOnly loadingFallback={<CartBenefitsBannerSkeleton currency={currency} />}>
            <CartBenefitsBanner
              isTrackingActive={isOpen}
              className={
                rewardGroup?.type === 'unlocks' && rewardGroup.reward.type === 'free_shipping'
                  ? 'desktop:rounded-b-none'
                  : undefined
              }
              onRewardClick={
                rewardGroup
                  ? () =>
                      open(
                        'Unlock reward',
                        <CartRewardDiscovery
                          currency={currency}
                          linkComponent={linkComponent}
                          onProductClick={onCartClose}
                          onCartMutation={quantityQueue.trackMutation}
                          initialGroup={rewardGroup.key}
                        />,
                      )
                  : undefined
              }
            />
          </QueryBoundary>
          <Condition
            expression={Boolean(isInitialLoading)}
            then={<CartRewardUpsellSkeleton />}
            else={
              <CartRewardUpsell
                group={bannerRewardGroup}
                currency={currency}
                linkComponent={linkComponent}
                onProductClick={onCartClose}
                onCartMutation={quantityQueue.trackMutation}
              />
            }
          />
          <div aria-hidden="true" className="-mx-4 mt-5 h-2 shrink-0 bg-neutral-50" />
          {[...filteredItems].reverse().map((item, index) => (
            <React.Fragment key={item.key}>
              <Condition
                expression={item.name === TEMP_ITEM_KEY}
                then={<CartProductCard.Skeleton isFirst={index === 0} />}
                else={
                  <CartProductCardWithPacks
                    item={item}
                    onImageClick={onCartClose}
                    isFirst={index === 0}
                    showDivider={index < filteredItems.length - 1}
                    currency={cartData.currency.currency}
                    quantityResetVersion={quantityQueue.resetVersion}
                    isUpdatingQuantity={quantityQueue.isUpdating}
                    isRemovalDisabled={quantityQueue.isUpdating || quantityQueue.hasQueued}
                    onQuantityChange={(quantity) => handleQuantityChange(item, quantity)}
                    onRemoveItem={() => handleDeleteItem(item)}
                    ImageComponent={ImageComponent}
                    linkComponent={linkComponent}
                  />
                }
              />
            </React.Fragment>
          ))}
          <CartSkinAnalysisRoutine
            currency={currency}
            linkComponent={linkComponent}
            onProductClick={onCartClose}
            onCartMutation={quantityQueue.trackMutation}
          />
          <QueryBoundary loadingFallback={<CartRecommendationsSkeleton />}>
            <CartRecommendations
              items={items}
              onCartMutation={quantityQueue.trackMutation}
              currency={currency}
              linkComponent={linkComponent}
              onProductClick={onCartClose}
            />
          </QueryBoundary>
          <div className="-mx-4">
            <CartWishlist
              onCartMutation={quantityQueue.trackMutation}
              currency={currency}
              linkComponent={linkComponent}
              onProductClick={onCartClose}
            />
            <CartRecentlyViewed
              onCartMutation={quantityQueue.trackMutation}
              currency={currency}
              linkComponent={linkComponent}
              onProductClick={onCartClose}
            />
          </div>
        </div>
      </div>
      {/* Box shadow, not a CSS filter: a filter layer here left the footer unpainted on some GPUs. */}
      <div className="w-full shrink-0">
        <motion.div
          initial={false}
          animate={{ height: isDetailsVisible ? 0 : 'auto', opacity: isDetailsVisible ? 0 : 1 }}
          transition={{ duration: shouldReduceMotion ? 0 : TRUST_TRANSITION_SECONDS, ease: 'easeInOut' }}
          aria-hidden={isDetailsVisible}
          className="overflow-hidden"
        >
          <CartTrustBadges />
        </motion.div>
        <div className="relative flex flex-col gap-3 w-full bg-generic-0 px-4 pt-3 pb-4 shadow-[0_0_7px_rgba(0,0,0,0.1)]">
          <div className="relative flex flex-col gap-5">
            <CartOrderSummary
              subtotal={totalProductPrice}
              itemCount={totalProductQuantity}
              deliveryFee={shippingCost}
              totalPrice={orderTotal}
              totalTax={totalTax}
              country={country}
              coupons={filteredCoupons}
              isFreeShipping={shippingCost === 0}
              freeShippingRemaining={cartData.progressBar?.freeShipping?.remaining}
              isDetailsVisible={isDetailsVisible}
              onDetailsVisibleChange={(visible) => {
                if (visible && !isDetailsVisible) {
                  cartAnalytics.track('order_details_opened');
                }
                setIsDetailsVisible(visible);
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <CartExpectedCreditRow currency={currency} />
            {quantityQueue.error && (
              <p role="alert" className="text-paragraph-small text-error-600">
                {quantityQueue.error}
              </p>
            )}
            <Button
              asChild
              size="lg"
              variant="primary"
              className={`h-12 w-full rounded-lg hover:bg-primary-400 active:bg-primary-400 ${CLASSNAME.NOT_TRANSLATE}`}
              onClick={handleCheckoutClick}
            >
              <LinkComponent href="/checkout/" className="no-underline hover:no-underline text-white">
                Checkout {totalProductQuantity} {totalProductQuantity === 1 ? 'item' : 'items'}
              </LinkComponent>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
